import { useState } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { useLingui } from '@lingui/react/macro'
import { PenIcon, TriangleAlertIcon } from 'lucide-react'

import { AddressFactuForm } from '~/domains/account/components/personal-infos/infos-factu-form'
import { useUserContext } from '~/providers/user-provider'

const UpdateDialog = () => {
	const { t } = useLingui()
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Dialog
			onOpenChange={setIsOpen}
			open={isOpen}
		>
			<DialogTrigger asChild>
				<Button
					className='ml-auto'
					size='sm'
					variant='link'
				>
					<PenIcon className='mr-1' />
					{t`Modifier`}
				</Button>
			</DialogTrigger>

			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t`Informations de facturation`}</DialogTitle>
				</DialogHeader>

				<AddressFactuForm onSuccess={() => setIsOpen(false)} />
			</DialogContent>
		</Dialog>
	)
}

const ErrorContent = () => {
	const { t } = useLingui()

	return (
		<div className='text-primary flex min-w-0 items-center gap-4 text-sm'>
			<TriangleAlertIcon
				className='!size-6 shrink-0'
				strokeWidth={1.25}
			/>

			<div className='leading-tight'>
				{t`Informations de facturation incomplètes`}
			</div>

			<UpdateDialog />
		</div>
	)
}

const ReadOnlyPlaceholder = () => {
	const { t } = useLingui()

	return (
		<div className='text-muted-foreground absolute inset-0 grid place-items-center text-sm'>
			{t`Informations disponibles après connexion`}
		</div>
	)
}

export interface FactuInfoDefaultValues {
	company?: string
	address?: string
	city?: string
	postal_code?: string
	country?: string
}

const FactuInfos = ({
	defaultValues,
}: {
	defaultValues?: FactuInfoDefaultValues
}) => {
	const { accountQuery } = useUserContext()

	const { postal_code, city, country, company, address } = {
		...defaultValues,
		...accountQuery.data?.billing_address,
	}

	const fullAddress = `${postal_code || ''} ${city || ''}, ${country || ''}`

	return (
		<div className='min-w-0 text-sm'>
			<div className='truncate font-bold'>{company || ''}</div>
			<div className='truncate'>{address || ''}</div>
			<div className='truncate'>{fullAddress}</div>
		</div>
	)
}

export const FactuInfosRecap = ({
	readOnly = false,
	defaultValues,
}: {
	readOnly?: boolean
	defaultValues?: FactuInfoDefaultValues
}) => {
	const { accountQuery } = useUserContext()

	const billingAddress = accountQuery.data?.billing_address

	const isInfoFactuIncomplete =
		(!billingAddress?.city && !defaultValues?.city) ||
		(!billingAddress?.country && !defaultValues?.country) ||
		(!billingAddress?.postal_code && !defaultValues?.postal_code) ||
		(!billingAddress?.address && !defaultValues?.address)

	return (
		<div className='flex flex-col gap-1'>
			{isInfoFactuIncomplete ? (
				readOnly ? (
					<ReadOnlyPlaceholder />
				) : (
					<ErrorContent />
				)
			) : (
				<FactuInfos defaultValues={defaultValues} />
			)}

			{!readOnly && !isInfoFactuIncomplete && (
				<div className='mt-auto flex flex-row print:hidden'>
					<UpdateDialog />
				</div>
			)}
		</div>
	)
}
